// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Skein — the DOM screens: menu, help, about, and the two results panels. Pure HTML builders
// (no state), wired up by main.ts. In Loom/Snarl the score is moves (fewer is better, "par" is
// about one drag per bead); in Cascade it is webs banked (more is better).

import { escapeHtml, moveLabel, webLabel } from './dom';
import { MODE_IDS, modeOf, type Mode } from './modes';
import type { PeerResult } from './race';

export const ATTRIB =
  'Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · ' +
  '<a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>';

/** Best-so-far label; Cascade counts up (more webs better), the others count down (fewer moves). */
export function bestText(mode: Mode, best: number | null): string {
  if (best === null || best < 0) return mode.stream ? 'No run yet' : 'No solve yet';
  return mode.stream ? `Best: ${webLabel(best)}` : `Best: ${moveLabel(best)}`;
}

function modeChips(modeId: string): string {
  return MODE_IDS.map(
    (id) =>
      `<button class="chip${id === modeId ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === modeId}">${escapeHtml(modeOf(id).name)}</button>`,
  ).join('');
}

export function menuHtml(o: { modeId: string; best: number | null; muted: boolean; dateStr: string }): string {
  const mode = modeOf(o.modeId);
  return `
    <section class="screen menu">
      <div class="brand">
        <div class="logo" aria-hidden="true"></div>
        <h1>Skein</h1>
        <p class="tagline">Drag the glowing beads until no threads cross.</p>
      </div>
      <div class="modepick">
        <div class="chips" role="group" aria-label="Choose a mode">${modeChips(o.modeId)}</div>
        <p class="mode-blurb">${escapeHtml(mode.blurb)}</p>
        <p class="mode-best">${bestText(mode, o.best)}</p>
      </div>
      <div class="menu-actions">
        <button class="btn primary" id="playRandom">Play</button>
        <button class="btn" id="playDaily">Daily web</button>
        <button class="btn" id="playFriends">Play with friends</button>
      </div>
      <div class="menu-links">
        <button class="linkbtn" id="help">How to play</button>
        <button class="linkbtn" id="about">About</button>
        <button class="linkbtn" id="mute">${o.muted ? 'Sound: off' : 'Sound: on'}</button>
      </div>
    </section>`;
}

export function helpHtml(): string {
  return `
    <h2>How to play</h2>
    <button class="sheet-close x" aria-label="Close">✕</button>
    <ul class="help-list">
      <li><strong>Drag a bead</strong> to move it. On a keyboard: <strong>[</strong> / <strong>]</strong> pick a bead, <strong>Space</strong> grabs and drops it, arrows nudge it.</li>
      <li>A thread turns <strong>coral with a ✕</strong> where it crosses another; a clean thread glows <strong>cyan</strong>.</li>
      <li>Move the beads until <strong>nothing crosses</strong> and the web settles green. Every web can be untangled.</li>
      <li>Fewer moves is a better solve — beat about one move per bead.</li>
      <li><strong>Cascade:</strong> a stream of small webs against a clock — bank as many as you can.</li>
      <li><strong>Snarl:</strong> one big web and a limited number of moves — run out and the run is over.</li>
    </ul>
    <button class="btn primary sheet-close">Got it</button>`;
}

export function aboutHtml(): string {
  return `
    <h2>About Skein</h2>
    <button class="sheet-close x" aria-label="Close">✕</button>
    <p>Untangle a web of threaded beads so that none of the threads cross. Every web is built
       from a seed and is guaranteed to have a tangle-free solution, so a friend on the same link
       untangles the identical web — race them for the fewest moves.</p>
    <p><strong>Multiplayer</strong> is peer-to-peer over WebRTC — there is no game server. Connecting to a friend
       uses a public signaling relay only to introduce the two browsers; no game data is stored on a server.</p>
    <p>No accounts, no cookies, no tracking. ${ATTRIB}</p>
    <button class="btn primary sheet-close">Close</button>`;
}

function medal(rank: number): string {
  return ['🥇', '🥈', '🥉'][rank] ?? `${rank + 1}.`;
}

function outcomeLabel(r: PeerResult, mode: Mode): string {
  if (r.left) return 'left';
  if (mode.stream) return r.done ? `${webLabel(r.solved)} · ${moveLabel(r.moves)}` : `${webLabel(r.solved)}…`;
  if (r.done && r.won) return `${moveLabel(r.moves)} · par ${r.par}`;
  if (r.done) return `out of moves · ${r.crossings} left`;
  return 'untangling…';
}

/** Solo results. */
export function soloResultsHtml(o: {
  mode: Mode;
  won: boolean;
  moves: number;
  par: number;
  solved: number;
  crossings: number;
  best: number | null;
  daily: boolean;
  isNewBest: boolean;
}): string {
  let headline: string;
  let sub: string;
  if (o.mode.stream) {
    headline = webLabel(o.solved);
    sub = o.isNewBest ? 'New best!' : 'Time — nice run';
  } else if (o.won) {
    const over = Math.max(0, o.moves - o.par);
    headline = moveLabel(o.moves);
    sub = o.isNewBest ? (over === 0 ? 'A clean solve — new best!' : 'New best!') : `par ${o.par}`;
  } else {
    headline = 'Out of moves';
    sub = `${o.crossings} thread${o.crossings === 1 ? '' : 's'} still crossing`;
  }
  return `
    <section class="screen results">
      <p class="res-mode">${escapeHtml(o.mode.name)}${o.daily ? ' · Daily' : ''}</p>
      <h2 class="res-time ${o.won || o.mode.stream ? 'win' : 'dnf'}">${escapeHtml(headline)}</h2>
      <p class="res-sub">${escapeHtml(sub)}</p>
      <p class="res-falls">${escapeHtml(bestText(o.mode, o.best))}</p>
    </section>`;
}

/** Multiplayer results: EVERY player's outcome (principle #9), plus the match tally. */
export function multiResultsHtml(ranked: PeerResult[], selfSeat: number | null, mode: Mode, tally: number[]): string {
  const win = mode.stream ? 'most webs wins' : 'fewest moves wins';
  const rows = ranked
    .map((r, i) => {
      const me = r.seat === selfSeat;
      const top = mode.stream ? r.solved > 0 && i === 0 : r.done && r.won && i === 0;
      return `<li class="res-row${me ? ' me' : ''}${r.left ? ' gone' : ''}">
        <span class="res-rank">${top || (r.done && r.won) ? medal(i) : '·'}</span>
        <span class="res-name">${escapeHtml(r.name)}${me ? ' <em>(you)</em>' : ''}</span>
        <span class="res-out">${escapeHtml(outcomeLabel(r, mode))}</span>
        <span class="res-wins">${tally[r.seat] ? '★'.repeat(Math.min(tally[r.seat], 5)) : ''}</span>
      </li>`;
    })
    .join('');
  return `
    <p class="res-mode">${escapeHtml(mode.name)} · ${win}</p>
    <ul class="res-list">${rows}</ul>`;
}
